(() => {
  'use strict';
  if (window.StadeskCartPricing) return;
  const root = window.Shopify?.routes?.root || '/';
  const service = 'https://node1.itoris.com/dpo/storefront/include.js';
  let busy = false;
  let ready = false;
  let pendingCheck = false;
  let pendingCartEvents = 0;
  let verifiedFingerprint = '';
  let failedSnapshot = '', failureMessage = '';
  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
  const controls = () => [...(document?.querySelectorAll('[data-stadesk-quantity]') || [])];
  const dpoId = item => (item.variant_title || item.title || '').match(/\(D#([A-Z0-9]+)\)/)?.[1] || '';
  // Liquid escapes property strings once; Ajax returns the original strings.
  // Decode only Liquid's pair-array representation, never the Ajax object.
  const liquidText = value => typeof value === 'string' ? value.replace(/&(?:amp|lt|gt|quot|#39|#x27);/g,
    entity => ({'&amp;':'&','&lt;':'<','&gt;':'>','&quot;':'"','&#39;':"'",'&#x27;':"'"})[entity]) : value;
  const properties = value => (Array.isArray(value) ? value.map(([key,val]) => [liquidText(key),liquidText(val)]) : Object.entries(value || {})).sort(([a], [b]) => a.localeCompare(b));
  const identity = item => JSON.stringify([String(item.variant_id), dpoId(item), properties(item.properties), String(item.selling_plan_allocation?.selling_plan?.id || '')]);
  function matched(items, expected) {
    const exact = items.find(item => item.key === expected.key);
    if (exact && identity(exact) === identity(expected)) return exact;
    const candidates = items.filter(item => identity(item) === identity(expected));
    return candidates.length === 1 ? candidates[0] : null;
  }
  function pairs(actual, expected) {
    if (actual.items.length !== expected.items.length) return null;
    const remaining = [...actual.items], result = [];
    for (const item of expected.items) {
      const found = matched(remaining, item);
      if (!found || found.quantity !== item.quantity) return null;
      remaining.splice(remaining.indexOf(found), 1); result.push([item, found]);
    }
    return result;
  }
  const fingerprint = cart => JSON.stringify([cart.currency, cart.total_price, cart.total_discount,
    cart.items.map(item => [identity(item), item.quantity, item.price, item.final_price, item.final_line_price]).sort((a,b) => JSON.stringify(a).localeCompare(JSON.stringify(b)))]);
  function controlItem(control, cart) {
    const exact = cart.items.find(item => item.key === control.dataset.key);
    if (!control.hasAttribute('data-variant-id') || !control.hasAttribute('data-line-properties')) return exact || null;
    let lineProperties;
    try { lineProperties = JSON.parse(control.dataset.lineProperties); } catch (_) { return null; }
    return matched(cart.items, {key:control.dataset.key, variant_id:control.dataset.variantId,
      variant_title:control.dataset.variantTitle, properties:lineProperties,
      selling_plan_allocation:{selling_plan:{id:control.dataset.sellingPlanId || ''}}});
  }
  const displayFingerprint = () => JSON.stringify(controls().map(control => [control.dataset.variantId || control.dataset.key,
    control.dataset.variantTitle, control.dataset.lineProperties, control.dataset.sellingPlanId,
    control.dataset.quantity, control.dataset.price]).sort((a,b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));
  async function refreshSections() {
    const sections = new Map();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(component => {
      sections.set(component.dataset.sectionId, component.hasAttribute('data-drawer') ? 'hydration' : 'full');
    });
    if (!sections.size) throw new Error('De winkelwagenweergave kon niet worden vernieuwd. Probeer opnieuw.');
    const {sectionRenderer} = await import('@theme/section-renderer');
    await Promise.all([...sections].map(([id, mode]) => sectionRenderer.renderSection(id, {cache:false, mode})));
  }
  async function reconcile(actual) {
    const stale = () => controls().some(control => {
      const item = controlItem(control, actual);
      return !item || item.quantity !== Number(control.dataset.quantity) || item.price !== Number(control.dataset.price);
    });
    if (stale()) {
      await refreshSections();
      if (stale()) throw new Error('De prijs is gecontroleerd, maar de winkelwagenweergave is nog niet bijgewerkt. Probeer opnieuw.');
      const latest = await cart();
      if (fingerprint(latest) !== fingerprint(actual)) throw new Error('Je winkelwagen is ondertussen gewijzigd. Probeer opnieuw.');
      actual = latest;
    }
    controls().forEach(control => {
      const item = controlItem(control, actual);
      if (!item) throw new Error('Deze winkelwagenregel kon niet veilig worden gekoppeld.');
      control.dataset.key = item.key;
      const row = control.closest('.cart-items__table-row[data-key]');
      if (row) { row.dataset.key = item.key; if (row.id?.startsWith('CartItem-')) row.id = 'CartItem-' + item.key; }
    });
  }
  function groups(cart) {
    const result = new Map();
    for (const item of cart.items) {
      const id = (item.variant_title || item.title || '').match(/\(D#([A-Z0-9]+)\)/)?.[1];
      if (!id) continue;
      let group = result.get(id);
      if (!group) result.set(id, group = {id, variant: item.variant_id, quantity: 0});
      if (Number(group.variant) !== Number(item.variant_id)) throw new Error('Deze configuratie kan niet automatisch worden herberekend.');
      group.quantity += item.quantity;
    }
    return [...result.values()];
  }
  async function json(url, options = {}) {
    const response = await fetch(url, {...options, signal: AbortSignal.timeout(20000)});
    const data = await response.json();
    if (!response.ok || data?.error || data?.notice || (data?.errors && Object.keys(data.errors).length)) throw new Error('De prijsservice kon de wijziging niet bevestigen.');
    return data;
  }
  const cart = () => json(root + 'cart.js', {cache:'no-store'});
  async function native(action, entries) {
    const body = new URLSearchParams();
    entries.forEach((entry, index) => {
      body.set(`items[${index}][dpo_cart_id]`, entry.id);
      body.set(`items[${index}][qty]`, entry.quantity);
      if (action === 'GetCartItemInfo') body.set(`variants[${entry.variant}]`, entry.quantity);
    });
    return json(`${service}?controller=${action}&shop=nofect.myshopify.com`, {method:'POST',body});
  }
  async function prices(entries) {
    if (!entries.length) return {};
    await native('UpdateCartItems', entries);
    for (let attempt = 0; attempt < 4; attempt++) {
      await wait(1000);
      const data = await native('GetCartItemInfo', entries);
      if (data.refresh_needed) continue;
      const valid = entries.every(entry => {
        const info = data.info?.[entry.id];
        return info && Number(info.variant_id) === Number(entry.variant) && Number(info.qty) === entry.quantity && Number.isFinite(Number(info.price)) && Number(info.price) > 0;
      });
      if (!valid) throw new Error('De nieuwe prijs is nog niet bevestigd. Probeer opnieuw.');
      return data.info;
    }
    throw new Error('De nieuwe prijs is nog niet beschikbaar. Probeer opnieuw.');
  }
  function verified(actual, expected, info) {
    const matches = pairs(actual, expected);
    if (!matches) return false;
    return matches.every(([item, found]) => {
      const id = (item.variant_title || item.title || '').match(/\(D#([A-Z0-9]+)\)/)?.[1];
      return !id || (info[id] && Math.abs(found.price - Math.round(Number(info[id].price) * 100)) <= 1);
    });
  }
  function sameQuantities(actual, expected) {
    return !!pairs(actual, expected);
  }
  async function verify(expected, info) {
    for (let attempt = 0; attempt < 4; attempt++) {
      const actual = await cart();
      if (verified(actual, expected, info)) return actual;
      if (!sameQuantities(actual, expected)) throw new Error('Je winkelwagen is ondertussen gewijzigd.');
      if (attempt === 0 && expected.items.length) await change(matched(actual.items, expected.items[0]).key, expected.items[0].quantity);
      await wait(1000);
    }
    throw new Error('De winkelwagen is gewijzigd of de prijs is nog niet bijgewerkt.');
  }
  const change = (key, quantity) => json(root + 'cart/change.js', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:key,quantity})});
  function status(message, error = false) {
    controls().forEach(control => {
      const output = control.querySelector('[data-stadesk-quantity-status]');
      if (output) { output.textContent = control.dataset['msg' + message[0].toUpperCase() + message.slice(1)] || ''; output.setAttribute('role', error ? 'alert' : 'status'); }
      control.querySelector('[data-stadesk-retry]')?.toggleAttribute('hidden', !error);
    });
  }
  function lock(value) {
    busy = value;
    document.querySelectorAll('cart-items-component button, cart-items-component input').forEach(element => {
      if (value && !element.disabled) { element.dataset.stadeskLocked = 'true'; element.disabled = true; }
      if (!value && element.dataset.stadeskLocked) { delete element.dataset.stadeskLocked; element.disabled = false; }
    });
    document.querySelectorAll('[name="checkout"], [data-stadesk-checkout]').forEach(button => { button.disabled = value || !ready; });
  }
  async function update(key, quantity) {
    if (busy || pendingCartEvents || !Number.isInteger(quantity) || quantity < 1 || quantity > 99) return;
    ready = false;
    verifiedFingerprint = '';
    lock(true);
    status('updating');
    let before;
    let proposed;
    let repriced = false;
    let mutationAttempted = false;
    try {
      before = await cart();
      const control = controls().find(control => control.dataset.key === key);
      const item = before.items.find(item => item.key === key) || (control && controlItem(control, before));
      if (!item) throw new Error('Je winkelwagen is gewijzigd. Vernieuw de pagina.');
      key = item.key;
      proposed = {...before, items:before.items.map(item => ({...item, quantity:item.key === key ? quantity : item.quantity}))};
      repriced = true;
      const info = await prices(groups(proposed));
      const latest = await cart();
      if (!sameQuantities(latest, before)) throw new Error('Je winkelwagen is gewijzigd. Probeer opnieuw.');
      mutationAttempted = true;
      await change(matched(latest.items, item).key, quantity);
      await verify(proposed, info);
      window.location.reload();
    } catch (error) {
      let restored = false;
      if (before && repriced) {
        try {
          const current = await cart();
          const old = before.items.find(item => item.key === key);
          const target = old && matched(current.items, old);
          if (mutationAttempted && old && target && sameQuantities(current, proposed) && target.quantity !== old.quantity) await change(target.key, old.quantity);
          const restoredCart = await cart();
          const info = await prices(groups(restoredCart));
          await verify(restoredCart, info);
          if (!sameQuantities(restoredCart, before)) { window.location.reload(); return; }
          restored = true;
        } catch (_) { restored = false; }
      }
      ready = restored && pendingCartEvents === 0;
      if (restored && before) controls().forEach(control => {
        const item = controlItem(control, before);
        if (item) control.querySelector('input').value = item.quantity;
      });
      status(restored ? 'restored' : 'unconfirmed', true);
      console.error('STADESK cart pricing:', error.message);
      lock(false);
      if (pendingCheck && !pendingCartEvents) { pendingCheck = false; window.queueMicrotask(() => initialize()); }
    }
  }
  async function initialize(force = false) {
    if (busy || pendingCartEvents) { pendingCheck = true; return; }
    if (!controls().length) { ready = true; verifiedFingerprint = ''; lock(false); return; }
    const wasReady = ready;
    ready = false;
    lock(true);
    if (!wasReady) status('checking');
    let attemptedCart;
    try {
      const before = await cart();
      attemptedCart = before;
      if (!force && failedSnapshot === fingerprint(before) + displayFingerprint()) throw new Error(failureMessage);
      let actual = before;
      if (force || fingerprint(before) !== verifiedFingerprint) {
        status('checking');
        const info = await prices(groups(before));
        actual = await verify(before, info);
      }
      attemptedCart = actual;
      await reconcile(actual);
      verifiedFingerprint = fingerprint(actual);
      failedSnapshot = ''; failureMessage = '';
      ready = pendingCartEvents === 0;
      status(ready ? 'ready' : 'checking');
    } catch(error) {
      verifiedFingerprint = '';
      failedSnapshot = attemptedCart ? fingerprint(attemptedCart) + displayFingerprint() : '';
      failureMessage = error.message;
      status('unconfirmed', true);
      console.error('STADESK cart pricing:', error.message);
    }
    previousSignature = signature();
    lock(false);
    if (pendingCheck && !pendingCartEvents) { pendingCheck = false; window.queueMicrotask(() => initialize()); }
  }
  document.addEventListener('click', event => {
    if (event.target.closest('[name="checkout"],a[href*="/checkout"]') && controls().length && !ready) {
      event.preventDefault(); event.stopImmediatePropagation(); return;
    }
    const retry = event.target.closest('[data-stadesk-retry]');
    if (retry) { event.preventDefault(); initialize(true); return; }
    const button = event.target.closest('[data-stadesk-quantity-update]');
    if (!button) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const control = button.closest('[data-stadesk-quantity]');
    const input = control.querySelector('input');
    if (input.reportValidity()) update(control.dataset.key, Number(input.value));
  }, true);
  document.addEventListener('submit', event => {
    if (controls().length && (!ready || event.target.querySelector('[data-stadesk-quantity] input:focus'))) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  window.addEventListener('pageshow', event => { if (event.persisted) initialize(true); });
  window.addEventListener('shopify:cart:lines-update', async event => {
    if (!controls().length) return;
    pendingCartEvents += 1;
    ready = false;
    document.querySelectorAll('[name="checkout"], [data-stadesk-checkout]').forEach(button => { button.disabled = true; });
    try { await event.promise; } catch (_) {}
    pendingCartEvents -= 1;
    window.requestAnimationFrame(() => initialize());
  });
  const signature = () => controls().map(control => [control.dataset.key, control.dataset.quantity, control.dataset.price].join(':')).join('|');
  let previousSignature = signature();
  new MutationObserver(() => {
    const next = signature();
    if (next === previousSignature) return;
    previousSignature = next;
    initialize();
  }).observe(document.body, {childList:true, subtree:true});
  window.StadeskCartPricing = {groups, verified, update, initialize};
  initialize();
})();
